import { z } from "zod/v4";
import { quantityTermSchema, quantityProducts, quantityProductsSummary } from "./quantity-products";

const value = z.number().finite().min(0).max(10_000_000);
const unit = z.enum(["m", "m²", "m³", "kg", "kg/m", "each"]);
const engineSource = z.enum(["measured", "assumed"]);
export const dimensionSchema = z.object({
  id: z.string().min(1).max(100),
  label: z.string().trim().min(1).max(120),
  value,
  unit,
  source: z.enum(["measured", "assumed", "entered"]),
  entryOrigin: z.literal("manual").optional(),
  note: z.string().max(1000).default(""),
  evidenceId: z.string().max(100).optional(),
  originalValue: value.optional(),
  originalSource: z.enum(["measured", "assumed", "entered"]).optional(),
}).superRefine((d, ctx) => {
  if (d.source === "assumed" && !d.note.trim())
    ctx.addIssue({ code: "custom", message: "Explain the assumed dimension", path: ["note"] });
  if (d.source === "entered" && d.entryOrigin !== "manual" && (d.originalValue == null || !d.originalSource))
    ctx.addIssue({ code: "custom", message: "Keep the engine's original value and source" });
});
export const quantityDerivationSchema = z.object({
  version: z.literal(1),
  dimensions: z.array(dimensionSchema).max(60),
  quantity_op: z.enum(["count", "length", "area", "sum_length_area", "sum_products", "wall_area", "net_area", "area_difference", "volume", "perimeter", "quantity", "direct"]),
  quantity_operands: z.array(z.string().max(100)).max(60),
  quantity_terms: z.array(quantityTermSchema).min(1).max(60).optional(),
  rationale: z.string().trim().min(1).max(2000),
  note: z.string().max(1000).default(""),
  quantity_overridden: z.boolean().default(false),
});
export type QuantityDerivation = z.infer<typeof quantityDerivationSchema>;
export type QuoteDimension = z.infer<typeof dimensionSchema>;
export const DIMENSION_FALLBACK_RATIONALE = "Included based on job description";
export const directDerivation = (rationale?: string, note = "Could not derive from files"): QuantityDerivation => ({
  version: 1, dimensions: [], quantity_op: "direct", quantity_operands: [],
  rationale: rationale?.trim() || DIMENSION_FALLBACK_RATIONALE, note, quantity_overridden: false,
});
const qtyUnit = (u: string) => ({
  sqm: "m²", m2: "m²", "m²": "m²", sqft: "sqft",
  m3: "m³", "m³": "m³", cum: "m³", cu_m: "m³", cubic_m: "m³", lm: "m", linear_m: "m", "linear m": "m",
  "linear metre": "m", "linear metres": "m", m: "m", metre: "m", metres: "m",
  each: "each", no: "each", nr: "each", item: "each", items: "each", unit: "each", allow: "each",
} as Record<string, string>)[u.toLowerCase().trim()] ?? u.toLowerCase().trim();

/** Closed operations only. No AI formula strings are ever evaluated. */
export function deriveDimensionQuantity(data: QuantityDerivation, lineUnit: string): number {
  const ids = new Set(data.dimensions.map(d => d.id));
  if (ids.size !== data.dimensions.length) throw new Error("Dimension IDs must be unique");
  const operands = data.quantity_operands.map(id => {
    const d = data.dimensions.find(x => x.id === id);
    if (!d) throw new Error("A quantity operand is missing");
    if (!Number.isFinite(d.value) || d.value < 0) throw new Error("Enter a non-negative, finite dimension");
    return d;
  });
  if (new Set(data.quantity_operands).size !== operands.length) throw new Error("A dimension cannot be used twice");
  const op = data.quantity_op;
  if (op === "sum_products") {
    const result = quantityProducts(data.quantity_terms, data.quantity_operands, data.dimensions, qtyUnit(lineUnit));
    if (!Number.isFinite(result) || result < 0 || result > 99_999_999)
      throw new Error("Excluded work exceeds included work, or the calculated quantity is out of range");
    return Math.round((result + Number.EPSILON) * 100) / 100;
  }
  const deductingArea = op === "wall_area" || op === "net_area";
  const areaDifference = op === "area_difference";
  const combinedArea = op === "sum_length_area";
  const n = op === "volume" ? 3 : ["area", "perimeter"].includes(op) ? 2 : deductingArea || areaDifference || combinedArea ? null : 1;
  if (op === "direct") throw new Error("Quantity entered directly — could not derive from files");
  if (n != null && operands.length !== n || op === "wall_area" && operands.length < 2 || op === "net_area" && operands.length < 3 || areaDifference && operands.length < 2 || combinedArea && operands.length < 3)
    throw new Error("This operation needs the correct number of dimensions");
  const expected = op === "quantity" ? qtyUnit(lineUnit) : op === "count" ? "each" : ["area", "sum_length_area", "wall_area", "net_area", "area_difference"].includes(op) ? "m²" : op === "volume" ? "m³" : "m";
  if (qtyUnit(lineUnit) !== expected) throw new Error("Dimensions and the quote's quantity unit must match");
  operands.forEach((d, i) => {
    const expectedInput = op === "quantity" ? expected : op === "count" ? "each" : areaDifference || deductingArea && i > 1 ? "m²" : "m";
    if (d.unit !== expectedInput) throw new Error(`Use ${expectedInput} for ${d.label}; units cannot be changed here`);
    if (op === "wall_area" && i > 1 && !/^Opening\b/i.test(d.label))
      throw new Error("Deductions must be labelled Opening and measured in m²");
    if (op === "net_area" && i > 1 && !/^Excluded\b/i.test(d.label))
      throw new Error("Net-area deductions must be labelled Excluded and measured in m²");
    if (areaDifference && i > 0 && !/^Excluded\b/i.test(d.label))
      throw new Error("Area deductions must be labelled Excluded and measured in m²");
  });
  const v = operands.map(d => d.value);
  let result = op === "area" ? v[0] * v[1]
    : combinedArea ? v[0] * v.slice(1).reduce((a, b) => a + b, 0)
    : deductingArea ? v[0] * v[1] - v.slice(2).reduce((a, b) => a + b, 0)
    : areaDifference ? v[0] - v.slice(1).reduce((a, b) => a + b, 0)
    : op === "volume" ? v[0] * v[1] * v[2]
    : op === "perimeter" ? 2 * (v[0] + v[1]) : v[0];
  if (result < 0) throw new Error(op === "net_area" || areaDifference
    ? "Excluded areas exceed the measured area; quantity has not changed."
    : "Openings exceed the wall area. Reduce the opening size; quantity has not changed.");
  if (!Number.isFinite(result) || result > 99_999_999) throw new Error("The calculated quantity is too large");
  return Math.round((result + Number.EPSILON) * 100) / 100;
}

export const quantityFormula = (op: QuantityDerivation["quantity_op"]) => ({
  sum_products: "included construction runs/areas − openings and excluded work",
  sum_length_area: "height × sum of lengths",
  net_area: "length × width − excluded areas",
  area_difference: "gross area − excluded areas",
  count: "count", length: "length", area: "length × width", wall_area: "length × height − openings",
  volume: "length × width × depth", perimeter: "2 × (length + width)", quantity: "entered measure", direct: "",
})[op];

export function editQuoteDimension(data: QuantityDerivation, id: string, nextValue: number, lineUnit: string, directQuantity?: number) {
  const next = quantityDerivationSchema.parse({
    ...data, quantity_overridden: false,
    dimensions: data.dimensions.map(d => d.id !== id ? d : ({
      ...d, value: nextValue, source: "entered",
      originalValue: d.originalValue ?? d.value,
      originalSource: d.originalSource ?? (d.entryOrigin === "manual" ? "entered" : d.source === "entered" ? "assumed" : d.source),
    })),
  });
  // A linear-priced line's editable length drives its metre quantity, even
  // when an older quote retained it as a direct quantity. Counts stay counts.
  const edited = next.dimensions.find(d => d.id === id);
  if (next.quantity_op === "direct" && qtyUnit(lineUnit) === "m" && edited?.unit === "m"
    && (/\b(length|span)$/i.test(edited.label) || next.dimensions.filter(d => d.unit === "m").length === 1)) {
    next.quantity_op = "length";
    next.quantity_operands = [id];
  }
  // Older/generated lines may retain complete measured axes but lose their
  // quantity formula during metadata validation. A dimension edit may restore
  // that link ONLY when the entire previous geometry reconciles with the
  // previous quantity. Never infer a gross area over missing deductions, extra
  // measurements, or a separately entered allowance. No work-item matching.
  if (next.quantity_op === "direct" && directQuantity != null && Number.isFinite(directQuantity)) {
    const unit = qtyUnit(lineUnit);
    const count = unit === "m²" ? 2 : unit === "m³" ? 3 : 0;
    if (count && data.dimensions.some(d => d.id === id)) {
      const axes = data.dimensions;
      const directMeasure = axes.length === 1 && axes[0].unit === unit;
      const geometric = axes.length === count && axes.every(d => d.unit === "m");
      const heights = axes.filter(d => /\bheight$/i.test(d.label));
      const lengths = axes.filter(d => /\b(length|width|span)$/i.test(d.label));
      const combinedArea = unit === "m²" && axes.length >= 3 && axes.every(d => d.unit === "m")
        && heights.length === 1 && lengths.length === axes.length - 1;
      if (directMeasure || geometric || combinedArea) {
        const measure = (original: boolean) => {
          const value = (d: QuoteDimension) => original ? d.originalValue ?? d.value : d.value;
          return combinedArea ? value(heights[0]) * lengths.reduce((n, d) => n + value(d), 0)
            : axes.reduce((n, d) => n * value(d), 1);
        };
        const reconciles = (value: number) => {
          const previous = Math.round(value * 10000) / 10000;
          const storedPrevious = Math.round((previous + Number.EPSILON) * 100) / 100;
          return Math.abs(previous - directQuantity) <= 0.00001 || Math.abs(storedPrevious - directQuantity) <= 0.00001;
        };
        if (reconciles(measure(false)) || reconciles(measure(true))) {
          next.quantity_op = directMeasure ? "quantity" : combinedArea ? "sum_length_area" : count === 2 ? "area" : "volume";
          next.quantity_operands = (combinedArea ? [heights[0], ...lengths] : axes).map(d => d.id);
          next.note = "Quantity linked to the retained measurements when edited; the previous measurements matched the previous quantity.";
        }
      }
    }
  }
  return { derivation: next, quantity: next.quantity_op === "direct" && directQuantity !== undefined
    ? directQuantity : deriveDimensionQuantity(next, lineUnit) };
}

/** Used at the save boundary: the persisted quantity must match the explanation. */
export function assertDimensionQuantity(data: QuantityDerivation, quantity: number, lineUnit: string) {
  if (!Number.isFinite(quantity) || quantity < 0) throw new Error("Quantity must be non-negative and finite");
  if (!data.quantity_overridden && data.quantity_op !== "direct"
    && Math.abs(deriveDimensionQuantity(data, lineUnit) - quantity) > 0.00001)
    throw new Error("Quantity does not match the dimensions. Edit a dimension or explicitly override Quantity.");
}

export function dimensionSummary(data: QuantityDerivation, quantity: number, lineUnit: string) {
  if (data.quantity_overridden) return "Quantity overridden — dimensions no longer drive this line";
  if (data.quantity_op === "direct") return "Quantity entered directly — could not derive from files";
  if (data.quantity_op === "sum_products")
    return `${quantityProductsSummary(data.quantity_terms, data.dimensions)} = ${quantity.toFixed(2)} ${lineUnit}`;
  const ds = data.quantity_operands.map(id => data.dimensions.find(d => d.id === id)!);
  const labels = ds.map(d => `${d.value.toFixed(2)} ${d.unit} (${d.source})`);
  const calculation = data.quantity_op === "sum_length_area" ? `${labels[0]} × (${labels.slice(1).join(" + ")})`
    : data.quantity_op === "area_difference" ? `${labels[0]} − (${labels.slice(1).join(" + ")})`
    : data.quantity_op === "wall_area" || data.quantity_op === "net_area" ? `${labels.slice(0, 2).join(" × ")}${labels.length > 2 ? ` − (${labels.slice(2).join(" + ")})` : ""}`
    : data.quantity_op === "perimeter" ? `2 × (${labels.join(" + ")})` : labels.join(" × ");
  return `${calculation} = ${quantity.toFixed(2)} ${lineUnit}`;
}

/** Missing/invalid model metadata is explicit direct quantity, never a guessed formula. */
export function retainGeneratedDimensions(raw: any, quantity: number, lineUnit: string, index = 0): QuantityDerivation {
  if (!Array.isArray(raw?.dimensions) && raw?.quantityDerivation && typeof raw.quantityDerivation === "object")
    raw = { ...raw, ...raw.quantityDerivation };
  const rationale = typeof raw?.rationale === "string" && raw.rationale.trim()
    ? raw.rationale.trim().slice(0, 2000) : DIMENSION_FALLBACK_RATIONALE;
  // One malformed operand must not erase all the other supported measurements.
  // Never accept model claims of builder entry or invented edit provenance.
  const dimensions: QuoteDimension[] = Array.isArray(raw?.dimensions) ? raw.dimensions.slice(0, 60).flatMap((dimension: unknown) => {
    const parsed = dimensionSchema.safeParse(dimension);
    return parsed.success && parsed.data.source !== "entered" && parsed.data.originalValue == null && !parsed.data.originalSource
      ? [parsed.data] : [];
  }) : [];
  const unambiguous = dimensions.filter(d => dimensions.filter(other => other.id === d.id).length === 1);
  const candidate = quantityDerivationSchema.safeParse({ ...raw, dimensions: unambiguous, version: 1, rationale, quantity_overridden: false });
  const data: QuantityDerivation = candidate.success ? candidate.data : {
    ...directDerivation(rationale), dimensions: unambiguous,
  };
  const names = new Map(data.dimensions.map(d => [d.id, `line-${index + 1}-${d.id}`]));
  data.dimensions = data.dimensions.map(d => ({ ...d, id: names.get(d.id)! }));
  data.quantity_operands = data.quantity_operands.map(id => names.get(id) ?? id);
  if (data.quantity_terms) data.quantity_terms = data.quantity_terms.map(t => ({
    ...t, operands: t.operands.map(id => names.get(id) ?? id),
  }));
  if (data.quantity_op === "direct") return { ...data, note: "Could not derive from files" };
  try { assertDimensionQuantity(data, quantity, lineUnit); return data; }
  catch (error) {
    return { ...data, quantity_op: "direct", quantity_operands: [],
      note: `Could not derive from files — ${error instanceof Error ? error.message : "dimensions did not reconcile with quantity"}` };
  }
}
